import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { analyticsApi, formAssignmentApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Stethoscope, Users, ShieldCheck, AlertTriangle, GraduationCap, Heart,
  ArrowRight, RefreshCw, Loader2, Activity, FileText, ClipboardList,
  TrendingUp, Settings, Megaphone, Building2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Dashboard for `director_patient_services` role.
 *
 * Cards map 1-to-1 to the six responsibility areas:
 *  1. Clinical Oversight
 *  2. Administrative Management
 *  3. Staff Leadership & Development
 *  4. Regulatory Compliance & QA
 *  5. Patient & Community Engagement
 *  6. Leadership in Absence of Administrator (admin shortcut tiles)
 */
export default function DirectorPatientServicesDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dueAssignments, setDueAssignments] = useState([]);
  const [staffByRole, setStaffByRole] = useState({});
  const [incidents, setIncidents] = useState({ count_30d: 0, recent: [] });
  const [requests, setRequests] = useState([]);
  const [compliance, setCompliance] = useState({ trained_pct: null });
  const [satisfactionForms, setSatisfactionForms] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [analyticsRes, dueRes, usersRes] = await Promise.all([
        analyticsApi.orgOverview().catch(() => ({ data: null })),
        formAssignmentApi.list({ due_within_days: 30 }).catch(() => ({ data: { assignments: [] } })),
        axios.get(`${API_URL}/api/admin/users`, { headers: authHeader() }).catch(() => ({ data: { users: [] } })),
      ]);
      setAnalytics(analyticsRes.data);
      setDueAssignments(dueRes.data.assignments || []);
      const users = usersRes.data.users || [];
      const byRole = {};
      users.forEach(u => { byRole[u.role] = (byRole[u.role] || 0) + 1; });
      setStaffByRole(byRole);

      // Optional widgets — silently empty on error.
      try {
        const incRes = await axios.get(`${API_URL}/api/incidents?days=30`, { headers: authHeader() });
        const list = incRes.data?.incidents || incRes.data || [];
        setIncidents({ count_30d: list.length || 0, recent: list.slice(0, 5) });
      } catch { /* ignore */ }

      try {
        const reqRes = await axios.get(`${API_URL}/api/service-requests?scope=org&limit=8`, { headers: authHeader() });
        setRequests(reqRes.data?.requests || []);
      } catch { /* ignore */ }

      try {
        const compRes = await axios.get(`${API_URL}/api/inservice/compliance/summary`, { headers: authHeader() });
        setCompliance({ trained_pct: compRes.data?.percent_compliant ?? null });
      } catch { /* ignore */ }

      try {
        const subRes = await axios.get(`${API_URL}/api/form-submissions?tag=satisfaction&limit=200`, { headers: authHeader() });
        const list = subRes.data?.submissions || subRes.data || [];
        setSatisfactionForms(Array.isArray(list) ? list.length : 0);
      } catch { /* ignore */ }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return (
      <div className="p-12 text-center text-[#64748B]" data-testid="director-loading">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading clinical dashboard…
      </div>
    );
  }

  const totalStaff = Object.values(staffByRole).reduce((a, b) => a + b, 0);
  const clinicalStaff = (staffByRole.nurse || 0) + (staffByRole.physician || 0) + (staffByRole.aide || 0) + (staffByRole.care_coordinator || 0);
  const openRequests = requests.filter(r => !['completed', 'cancelled'].includes(r.status)).length;

  return (
    <div className="space-y-6" data-testid="director-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#64748B] mt-1">Director of Patient Services — clinical leadership cockpit.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="dps-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<Heart className="w-5 h-5 text-rose-500" />}
          label="Active patients"
          value={analytics?.active_patients ?? analytics?.patients?.active ?? '—'}
          testId="kpi-patients"
        />
        <KpiCard
          icon={<Users className="w-5 h-5 text-sky-600" />}
          label="Clinical staff"
          value={`${clinicalStaff} / ${totalStaff}`}
          testId="kpi-staff"
        />
        <KpiCard
          icon={<GraduationCap className="w-5 h-5 text-emerald-600" />}
          label="In-service compliance"
          value={compliance.trained_pct !== null ? `${Math.round(compliance.trained_pct)}%` : '—'}
          testId="kpi-compliance"
        />
        <KpiCard
          icon={<AlertTriangle className="w-5 h-5 text-amber-600" />}
          label="Incidents (30d)"
          value={incidents.count_30d}
          testId="kpi-incidents"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* 1. Clinical Oversight ---------------------------------------- */}
        <SectionCard
          title="Clinical Oversight"
          icon={<Stethoscope className="w-4 h-4 text-[#0E7490]" />}
          testId="clinical-oversight"
          action={<Link to="/patients" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">Patients <ArrowRight className="w-3 h-3" /></Link>}
        >
          <Row label="Active patients" value={analytics?.active_patients ?? '—'} />
          <Row label="Open care plans" value={analytics?.open_care_plans ?? analytics?.care_plans?.open ?? '—'} />
          <Row label="Open clinical notes" value={analytics?.open_clinical_notes ?? '—'} />
          <Row label="Forms due in 30 days" value={dueAssignments.length} valueLink="/forms/assignments" />
        </SectionCard>

        {/* 2. Administrative -------------------------------------------- */}
        <SectionCard
          title="Administrative Management"
          icon={<Building2 className="w-4 h-4 text-[#0E7490]" />}
          testId="administrative"
          action={<Link to="/subscription" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">Subscriptions <ArrowRight className="w-3 h-3" /></Link>}
        >
          <Row label="Total staff" value={totalStaff} valueLink="/admin/users" />
          <Row label="Open service requests" value={openRequests} valueLink="/admin/service-requests" />
          <Row label="Access requests" value={'—'} valueLink="/admin/access-requests" />
          <Row label="Subscription plan" value={analytics?.subscription_plan || '—'} valueLink="/subscription" />
        </SectionCard>

        {/* 3. Staff Leadership & Development ---------------------------- */}
        <SectionCard
          title="Staff Leadership & Development"
          icon={<Users className="w-4 h-4 text-[#0E7490]" />}
          testId="staff-leadership"
          action={<Link to="/admin/users" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">Manage users <ArrowRight className="w-3 h-3" /></Link>}
        >
          {Object.entries(staffByRole).slice(0, 6).map(([role, n]) => (
            <Row key={role} label={role.replace(/_/g, ' ')} value={n} />
          ))}
          <Row label="In-service compliance" value={compliance.trained_pct !== null ? `${Math.round(compliance.trained_pct)}%` : '—'} valueLink="/inservice" />
        </SectionCard>

        {/* 4. Regulatory & QA ------------------------------------------- */}
        <SectionCard
          title="Regulatory Compliance & QA"
          icon={<ShieldCheck className="w-4 h-4 text-[#0E7490]" />}
          testId="regulatory-qa"
          action={<Link to="/incidents" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">Incidents <ArrowRight className="w-3 h-3" /></Link>}
        >
          <Row label="Incidents (30d)" value={incidents.count_30d} />
          <Row label="Open service requests" value={openRequests} valueLink="/admin/service-requests" />
          <Row label="Audit log activity" value={'View'} valueLink="/admin/audit-logs" />
          {incidents.recent.length > 0 && (
            <ul className="mt-2 space-y-1">
              {incidents.recent.map((i, idx) => (
                <li key={i.id || idx} className="text-xs text-[#64748B] truncate">
                  <span className="text-[#94A3B8]">•</span> {i.summary || i.title || 'Incident'} <span className="text-[#94A3B8]">— {(i.created_at || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* 5. Patient & Community Engagement ---------------------------- */}
        <SectionCard
          title="Patient & Community Engagement"
          icon={<Megaphone className="w-4 h-4 text-[#0E7490]" />}
          testId="engagement"
        >
          <Row label="Patient satisfaction forms" value={satisfactionForms} valueLink="/forms" />
          <Row label="Telehealth sessions" value={analytics?.telehealth_sessions ?? '—'} valueLink="/telehealth" />
          <Row label="Marketing campaigns" value={'View'} valueLink="/crm/campaigns" />
        </SectionCard>

        {/* 6. Leadership in Absence of Administrator -------------------- */}
        <SectionCard
          title="Leadership in Absence of Administrator"
          icon={<Settings className="w-4 h-4 text-[#0E7490]" />}
          testId="leadership-absent"
        >
          <p className="text-xs text-[#64748B] mb-2">When covering for the practice admin you have full operational reach:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <ShortcutTile to="/admin/users" label="Users" icon={<Users className="w-3.5 h-3.5" />} />
            <ShortcutTile to="/subscription" label="Subscriptions" icon={<TrendingUp className="w-3.5 h-3.5" />} />
            <ShortcutTile to="/incidents" label="Incidents" icon={<AlertTriangle className="w-3.5 h-3.5" />} />
            <ShortcutTile to="/admin/service-requests" label="Service Requests" icon={<ClipboardList className="w-3.5 h-3.5" />} />
            <ShortcutTile to="/forms" label="Forms" icon={<FileText className="w-3.5 h-3.5" />} />
            <ShortcutTile to="/admin/audit-logs" label="Audit log" icon={<Activity className="w-3.5 h-3.5" />} />
          </div>
        </SectionCard>
      </div>

      {/* Recent service requests ---------------------------------------- */}
      <Card className="border-[#E2E8F0]" data-testid="recent-service-requests">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-[#0A2342] flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#0E7490]" /> Recent service requests
          </CardTitle>
          <Link to="/admin/service-requests" className="text-xs text-[#0E7490] hover:underline">View all →</Link>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">No requests yet.</p>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]">
              {requests.slice(0, 8).map(r => (
                <li key={r.id} className="py-2 text-sm flex items-center justify-between gap-3" data-testid={`recent-request-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/service-requests/${r.id}`} className="font-medium text-[#0A2342] hover:text-[#0E7490] hover:underline truncate block">
                      {r.title}
                    </Link>
                    <p className="text-[10px] text-[#94A3B8]">{r.type_name || '—'} · {r.requester_name || '—'} · {(r.created_at || '').slice(0, 10)}</p>
                  </div>
                  <Badge className={statusBadge(r.status)} variant="outline">{(r.status || '').replace('_', ' ')}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon, label, value, testId }) {
  return (
    <Card className="border-[#E2E8F0]" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-[#64748B]">{icon} {label}</div>
        <div className="text-2xl font-bold text-[#0A2342] mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, icon, action, testId, children }) {
  return (
    <Card className="border-[#E2E8F0]" data-testid={testId}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">{icon} {title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="space-y-1">{children}</CardContent>
    </Card>
  );
}

function Row({ label, value, valueLink }) {
  const v = <span className="font-semibold text-[#0A2342]">{value}</span>;
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-[#F8FAFC] last:border-0">
      <span className="text-[#64748B] capitalize">{label}</span>
      {valueLink ? <Link to={valueLink} className="hover:underline">{v}</Link> : v}
    </div>
  );
}

function ShortcutTile({ to, label, icon }) {
  return (
    <Link to={to} className="px-2 py-2 rounded-md border border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#0E7490]/5 flex items-center gap-2 text-[#0A2342]">
      <span className="text-[#0E7490]">{icon}</span>
      <span className="text-xs font-medium">{label}</span>
    </Link>
  );
}

function statusBadge(status) {
  switch ((status || '').toLowerCase()) {
    case 'open': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'in_progress': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'blocked': return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'cancelled': return 'bg-slate-50 text-slate-600 border-slate-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}
